import { LOCALES, LOCALE_NAME, useLocale, type Locale } from '../lib/i18n'
import styles from './LocalePicker.module.css'

/**
 * THE LOCALE CONTROL — language design §5, "locale selection at splash/profile".
 *
 * A NATIVE `<select>`, and the reasons are all about the languages that are not here yet
 * (founder, 2026-08-21: "in the future, i want to support basically a large amount of
 * languages"). Three words in a row was right at three and is a wall of text at fifteen. One
 * element costs the same at 3 or 50, and the platform gives us, free: the OS-native picker on
 * mobile (a proper sheet, not a row of small targets), keyboard and screen-reader support,
 * type-ahead once the list is long enough to need it. The cost is one extra tap. Worth it.
 *
 * ⚠ NO FLAGS, and this is a decision rather than an omission (founder-ruled, same day).
 * A flag is a country and a language is not one. Spanish: Spain's flag tells ~130M Mexicans
 * their language belongs to Europe, and Mexico's tells everyone else the reverse. English: the
 * Union Jack or the Stars and Stripes, each wrong for the other and for the Indian, Nigerian
 * and Australian readers who outnumber both. It fails backwards too — Switzerland has four
 * national languages and one flag. Icelandic is the one clean case, which is exactly why flags
 * feel harmless while this product has three languages, and exactly why designing from it
 * breaks at fifteen. There is also a plain rendering fact: Segoe UI Emoji ships no
 * regional-indicator glyphs, so a flag renders on Windows as the two letters of its code.
 *
 * THE ENDONYM, ALWAYS. A reader looking for Icelandic scans for "Íslenska" — never for what
 * English calls it, and never for a flag they have to decode first.
 *
 * ⚠ IT DOES NOT TRANSLATE THE FICTION. Pack titles, blurbs and opening cards are designer-owned
 * content per language (§5). Where a pack has no copy in the chosen language, its card stays
 * English inside translated chrome — the honest state, not a bug to paper over.
 */
export default function LocalePicker({
  variant = 'quiet',
  className,
}: {
  /**
   * PROMINENT ON THE FIRST PAGE, QUIET AFTERWARDS (founder, 2026-08-21).
   *
   * The splash is first contact and language is a PRECONDITION to the only decision on it —
   * a reader who cannot read "Continue with Google" needs this before they need anything.
   * Everywhere after, the choice is made and a loud control would be chrome about chrome.
   *
   * The two differ in PLACEMENT as well as contrast, and the placement is the substantive half:
   * `prominent` sits in the reading path between the wordmark and the doors, `quiet` returns to
   * the corner. The corner is the convention for a language SETTING, and borrowing it on the way
   * in was the wrong convention for a choice that is part of the threshold.
   */
  variant?: 'prominent' | 'quiet'
  className?: string
}) {
  const { locale, setLocale, t } = useLocale()

  return (
    <div
      className={[styles.picker, styles[variant], className]
        .filter(Boolean)
        .join(' ')}
    >
      {/*
        The select carries the accessible name itself rather than leaning on a visible <label>.
        A label would have to read "language" in the language the reader cannot yet read — and
        beside a wordmark it is chrome about chrome. The endonyms are their own label.
      */}
      <select
        className={styles.select}
        aria-label={t('locale.label')}
        value={locale}
        onChange={(e) => setLocale(e.target.value as Locale)}
      >
        {LOCALES.map((l) => (
          // `lang` per option so a screen reader pronounces each name in its own language
          // rather than reading "Íslenska" through the page's phonology.
          <option key={l} value={l} lang={l}>
            {LOCALE_NAME[l]}
          </option>
        ))}
      </select>
      {/* The chevron. Drawn rather than native so the control can stay this quiet — a default
          select chrome would be the loudest thing on the splash. */}
      <span className={styles.chevron} aria-hidden="true">
        ▾
      </span>
    </div>
  )
}
